import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { useAuth } from './hooks/useAuth';
import type { StoreMenu, UserInfo } from './types';

// 布局
import StoreLayout from './components/StoreLayout';

// 公开页面
const Login = React.lazy(() => import('./pages/Login'));
const SelectStore = React.lazy(() => import('./pages/SelectStore'));

// 业务页面
const StoreDashboard = React.lazy(() => import('./pages/dashboard/StoreDashboard'));
const Cashier = React.lazy(() => import('./pages/business/Cashier'));
const OrderManage = React.lazy(() => import('./pages/business/OrderManage'));
const OrderDetail = React.lazy(() => import('./pages/business/OrderDetail'));
const MallOrderManage = React.lazy(() => import('./pages/business/MallOrderManage'));
const WashOrderManage = React.lazy(() => import('./pages/business/WashOrderManage'));
const BoardingOrderManage = React.lazy(() => import('./pages/business/BoardingOrderManage'));
const MedicalOrderList = React.lazy(() => import('./pages/MedicalOrderList'));
const MedicalConfig = React.lazy(() => import('./pages/MedicalConfig'));
const ContractOrderManage = React.lazy(() => import('./pages/contract/ContractOrderManage'));
const SettlementManage = React.lazy(() => import('./pages/contract/SettlementManage'));
const PayrollManage = React.lazy(() => import('./pages/contract/PayrollManage'));
const ProductManage = React.lazy(() => import('./pages/product/ProductManage'));
const ProductEdit = React.lazy(() => import('./pages/product/ProductEdit'));
const CategoryManage = React.lazy(() => import('./pages/product/CategoryManage'));
const ServiceManage = React.lazy(() => import('./pages/product/ServiceManage'));
const LiveAnimal = React.lazy(() => import('./pages/product/LiveAnimal'));
const MaterialManage = React.lazy(() => import('./pages/product/MaterialManage'));
const MemberManage = React.lazy(() => import('./pages/customer/MemberManage'));
const MemberCreate = React.lazy(() => import('./pages/customer/MemberCreate'));
const MemberDetail = React.lazy(() => import('./pages/customer/MemberDetail'));
const BalanceLog = React.lazy(() => import('./pages/customer/BalanceLog'));
const PromotionRelationList = React.lazy(() => import('./pages/customer/PromotionRelationList'));
const BusinessReport = React.lazy(() => import('./pages/statistics/BusinessReport'));
const SalesReport = React.lazy(() => import('./pages/statistics/SalesReport'));
const MemberReport = React.lazy(() => import('./pages/statistics/MemberReport'));
const StoreSettings = React.lazy(() => import('./pages/settings/StoreSettings'));
const StaffManage = React.lazy(() => import('./pages/settings/StaffManage'));
const MessagePush = React.lazy(() => import('./pages/settings/MessagePush'));
const StoreCode = React.lazy(() => import('./pages/settings/StoreCode'));
const PermissionManage = React.lazy(() => import('./pages/settings/PermissionManage'));

const PageFallback = (
  <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin />
  </div>
);

const withPageSuspense = (node: React.ReactNode) => (
  <Suspense fallback={PageFallback}>{node}</Suspense>
);

const collectMenuRoutePaths = (menus: StoreMenu[] = [], result: string[] = []) => {
  menus.forEach((menu) => {
    if (menu.route_path) {
      result.push(menu.route_path);
    }
    collectMenuRoutePaths(menu.children || [], result);
  });
  return result;
};

const getDefaultRoutePath = (user: UserInfo | null, canAccessDashboard: boolean) => {
  if (canAccessDashboard) {
    return '/dashboard';
  }
  const routes = (user?.routes || []).filter((route) => typeof route === 'string' && route.startsWith('/'));
  if (routes.length > 0) {
    return routes[0];
  }
  const menuRoutes = collectMenuRoutePaths(user?.menus || []);
  return menuRoutes[0] || '';
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, authReady } = useAuth();
  if (loading || !authReady) {
    return PageFallback;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PageGate: React.FC<{ menuCode: string; children: React.ReactNode }> = ({ menuCode, children }) => {
  const { loading, authReady, rbacReady, hasPage } = useAuth();
  if (loading || !authReady || !rbacReady) {
    return PageFallback;
  }
  if (!hasPage(menuCode)) {
    return <Result status="403" title="403" subTitle="无权访问该页面" />;
  }
  return <>{children}</>;
};

const withPage = (menuCode: string, node: React.ReactNode) => (
  <PageGate menuCode={menuCode}>{node}</PageGate>
);

const DefaultRoute: React.FC = () => {
  const { user, loading, authReady, rbacReady, hasPage } = useAuth();
  if (loading || !authReady || !rbacReady) {
    return PageFallback;
  }
  const targetPath = getDefaultRoutePath(user, hasPage('dashboard'));
  if (!targetPath) {
    return <Result status="403" title="403" subTitle="暂无可访问页面" />;
  }
  return <Navigate to={targetPath} replace />;
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* 公开路由 */}
      <Route path="/login" element={withPageSuspense(<Login />)} />
      <Route path="/select-store" element={withPageSuspense(<SelectStore />)} />

      {/* 受保护路由：StoreLayout 包裹 */}
      <Route
        element={
          <PrivateRoute>
            <StoreLayout />
          </PrivateRoute>
        }
      >
        {/* 首页 */}
        <Route path="/" element={<DefaultRoute />} />
        <Route path="/dashboard" element={withPage('dashboard', <StoreDashboard />)} />

        {/* 店面业务 */}
        <Route path="/business/cashier" element={withPage('business.cashier', <Cashier />)} />
        <Route path="/business/orders" element={withPage('business.orders', <OrderManage />)} />
        <Route path="/business/orders/:orderCode" element={withPage('business.orders', <OrderDetail />)} />
        <Route path="/business/mall-orders" element={withPage('business.mall_orders', <MallOrderManage />)} />
        <Route path="/business/wash-orders" element={withPage('business.wash_orders', <WashOrderManage />)} />
        <Route path="/business/boarding-orders" element={withPage('business.boarding_orders', <BoardingOrderManage />)} />
        <Route path="/business/medical" element={withPage('business.medical', <MedicalOrderList />)} />
        <Route path="/business/medical/config" element={withPage('business.medical', <MedicalConfig />)} />

        {/* 资金结算 */}
        <Route path="/contract/orders" element={withPage('contract.orders', <ContractOrderManage />)} />
        <Route path="/contract/settlement" element={withPage('contract.settlement', <SettlementManage />)} />
        <Route path="/contract/payroll" element={withPage('contract.payroll', <PayrollManage />)} />

        {/* 商品管理 */}
        <Route path="/product/goods" element={withPage('product.goods', <ProductManage />)} />
        <Route path="/product/goods/create" element={withPage('product.goods_create', <ProductEdit />)} />
        <Route path="/product/goods/edit/:code" element={withPage('product.goods', <ProductEdit />)} />
        <Route path="/product/category" element={withPage('product.category', <CategoryManage />)} />
        <Route path="/product/service" element={withPage('service.product', <Navigate to="/service/product" replace />)} />
        <Route path="/product/live-animal" element={withPage('service.live_animal', <Navigate to="/service/live-animal" replace />)} />
        <Route path="/service/product" element={withPage('service.product', <ServiceManage />)} />
        <Route path="/service/live-animal" element={withPage('service.live_animal', <LiveAnimal />)} />
        {/* 素材管理 */}
        <Route path="/material/list" element={withPage('material.list', <MaterialManage />)} />

        {/* 客户中心 */}
        <Route path="/customer/members" element={withPage('customer.members', <MemberManage />)} />
        <Route path="/customer/members/create" element={withPage('customer.members', <MemberCreate />)} />
        <Route path="/customer/members/detail/:id" element={withPage('customer.members', <MemberDetail />)} />
        <Route path="/customer/balance-logs" element={withPage('customer.balance_logs', <BalanceLog />)} />
        <Route path="/customer/promotions" element={withPage('customer.promotions', <PromotionRelationList />)} />

        {/* 数据统计 */}
        <Route path="/statistics/business" element={withPage('statistics.business', <BusinessReport />)} />
        <Route path="/statistics/sales" element={withPage('statistics.sales', <SalesReport />)} />
        <Route path="/statistics/members" element={withPage('statistics.members', <MemberReport />)} />

        {/* 基础配置 */}
        <Route path="/settings/store" element={withPage('settings.store', <StoreSettings />)} />
        <Route path="/settings/staff" element={withPage('settings.staff', <StaffManage />)} />
        <Route path="/settings/message" element={withPage('settings.message', <MessagePush />)} />
        <Route path="/settings/store-code" element={withPage('settings.store_code', <StoreCode />)} />
        <Route path="/settings/permission" element={withPage('settings.permission', <PermissionManage />)} />
      </Route>
    </Routes>
  );
};

export default App;
